/**
 * Bundle builder (templates/_components/forms/product/bundle-builder.twig).
 *
 * The customer picks one product per category and its size; the form then adds them all at once.
 * Posting, the mini cart, the drawer and add_to_cart tracking come from addToCart() (cart.js); the
 * hidden purchasables[…] inputs follow the selection.
 *
 * config.categories: [{ id, products: [{ id, variants: [{ id, title, price, hasStock, trackItem }] }] }]
 * config.discountPercent: the bundle coupon's percentage, 0 when the bundle has no live coupon
 * config.locale, config.currency: for the amounts
 *
 * `selected` holds the pick per category: { categoryId: { productId, variantId } }.
 */
window.bundleBuilder = function (config) {
    const money = new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency });

    return {
        ...window.addToCart(),
        categories: config.categories,
        discountPercent: config.discountPercent,
        activeId: config.categories[0]?.id ?? null,
        selected: {},

        product(categoryId) {
            const pick = this.selected[categoryId];
            const category = this.categories.find(category => category.id === categoryId);
            return pick && category ? category.products.find(product => product.id === pick.productId) ?? null : null;
        },

        variant(categoryId) {
            const pick = this.selected[categoryId];
            return this.product(categoryId)?.variants.find(variant => variant.id === pick.variantId) ?? null;
        },

        isPicked(categoryId, productId) {
            return this.selected[categoryId]?.productId === productId;
        },

        // Picks the product with its first size in stock and moves on to the next category without a pick
        pick(categoryId, productId) {
            const product = this.categories.find(category => category.id === categoryId)?.products.find(product => product.id === productId);
            if (!product) {
                return;
            }
            const variant = product.variants.find(variant => variant.hasStock) ?? null;
            this.selected[categoryId] = { productId, variantId: variant ? variant.id : null };

            const next = this.categories.find(category => !this.selected[category.id]);
            if (next) {
                this.activeId = next.id;
            }
        },

        pickVariant(categoryId, variant) {
            if (variant.hasStock && this.selected[categoryId]) {
                this.selected[categoryId].variantId = variant.id;
            }
        },

        // The picked variants in category order, for the hidden inputs and the totals
        get lines() {
            return this.categories
                .map(category => this.variant(category.id))
                .filter(variant => variant && variant.hasStock);
        },

        get complete() {
            return this.lines.length === this.categories.length;
        },

        get total() {
            return this.lines.reduce((sum, variant) => sum + variant.price, 0);
        },

        get discount() {
            return this.total * this.discountPercent / 100;
        },

        format(amount) {
            return money.format(amount);
        },

        // add_to_cart with every product of the bundle (replaces addToCart()'s single item)
        trackAddToCart() {
            if (!window.track) {
                return;
            }

            // Tracking never gets in the way of the cart itself
            try {
                const items = this.lines.map(variant => ({ ...variant.trackItem, quantity: 1 }));
                window.track({
                    event: 'add_to_cart',
                    ecommerce: {
                        currency: config.currency,
                        // As addToCart() counts it: the items' price before promotions
                        value: Number(items.reduce((sum, item) => sum + item.price, 0).toFixed(2)),
                        items,
                    },
                });
            } catch (error) {
                console.error('Tracking: bundle add_to_cart failed', error);
            }
        },
    };
};
